import { useState, useEffect } from "react";
import { apiFetch } from "../../api/api";
import { T } from "../data/Tokens";
import {
  Card, PageHeader, GoldBtn, OutlineBtn, GhostBtn, StatusPill,
  SearchBar, Avatar, Th, Td, Tr, Tabs, ConfirmModal, EmptyState, StatCard
} from "../components/common/Ui";

const PRODUCTS_LIST = [
  "Getting Things Done", "Midnight Mirage", "Initials A", "Groove Planner",
  "Travel Planner", "Bridal Planner", "Wellness Journal", "Recipe Journal",
  "Daily Planner 2026", "Gratitude Journal", "Timeless Photobook",
];

  const EMPTY_ORDER = {
  customer:"", email:"", phone:"", city:"", address:"",
  product:"", amount:"", pay:"Pending", status:"Pending", track:"", notes:"",
};

const STATUS_LIST  = ["Pending","Processing","Packed","Shipped","Delivered","Cancelled","Returned","Refunded"];
const PAYMENT_LIST = ["Pending","Paid","Failed","Refunded"];

// Order ka payment status column jo dikhana hai — COD orders jinka payment
// abhi tak "Pending" hai unke liye "Pending" ki jagah "COD" dikhao (data database
// mein waisa hi Pending rehta hai, sirf yahan display ke liye label badalte hain).
const paymentLabel = (o) => (o.payMethod === "COD" && o.pay === "Pending") ? "COD" : o.pay;

// ── Social links & WhatsApp message builder ─────────────────────────────────
const SOCIAL_LINKS = {
  instagram: "https://www.instagram.com/aishcrafts.comm?igsh=ejdjdWw2N3Zkd2Vt",
  facebook: "https://www.facebook.com/share/193sAmQyZ9/",
  website: "https://aishcraft.com",
};

const cleanPhoneForWhatsApp = (phone) => {
  if (!phone) return null;
  let digits = phone.replace(/[^\d]/g, "");
  if (digits.length === 10) digits = "91" + digits;
  if (!digits) return null;
  return digits;
};

const buildWhatsAppMessage = (order) => {
  return [
    `Hi ${order.customer || "there"}! 🙏`,
    ``,
    `Thank you for shopping with Aishcraft ❤️`,
    ``,
    `🧾 Order ID: ${order.id}`,
    `🎁 Product: ${order.product || "—"}`,
    `📌 Status: ${order.status}`,
    ``,
    `Stay connected with us:`,
    `📸 Instagram: ${SOCIAL_LINKS.instagram}`,
    `📘 Facebook: ${SOCIAL_LINKS.facebook}`,
    ``,
    `Thanks for being part of the Aishcraft family! 🎉`,
  ].join("\n");
};

const buildWhatsAppLink = (order) => {
  const phone = cleanPhoneForWhatsApp(order.phone);
  const message = buildWhatsAppMessage(order);
  const base = phone ? `https://wa.me/${phone}` : `https://wa.me/`;
  return `${base}?text=${encodeURIComponent(message)}`;
};



// ── Input helper ──────────────────────────────────────────────────────────────
const FInput = ({ label, value, onChange, placeholder, type="text", required }) => (
  <div>
    <label className="block text-[11px] font-semibold mb-1.5 uppercase tracking-wider" style={{ color:T.textSec }}>
      {label}{required && <span style={{ color:T.danger }}> *</span>}
    </label>
    <input type={type} value={value} onChange={onChange} placeholder={placeholder}
      className="w-full px-3 py-2.5 rounded-xl text-[13px] outline-none transition-all"
      style={{ background:T.bgSoft, border:`1px solid ${T.border}`, color:T.textPri }}
      onFocus={e=>e.target.style.borderColor=T.gold}
      onBlur={e=>e.target.style.borderColor=T.border}/>
  </div>
);

const FSelect = ({ label, value, onChange, children, required }) => (
  <div>
    <label className="block text-[11px] font-semibold mb-1.5 uppercase tracking-wider" style={{ color:T.textSec }}>
      {label}{required && <span style={{ color:T.danger }}> *</span>}
    </label>
    <select value={value} onChange={onChange}
      className="w-full px-3 py-2.5 rounded-xl text-[13px] outline-none"
      style={{ background:T.bgSoft, border:`1px solid ${T.border}`, color:T.textPri }}>
      {children}
    </select>
  </div>
);

const FTextarea = ({ label, value, onChange, placeholder, rows=3 }) => (
  <div>
    <label className="block text-[11px] font-semibold mb-1.5 uppercase tracking-wider" style={{ color:T.textSec }}>{label}</label>
    <textarea value={value} onChange={onChange} placeholder={placeholder} rows={rows}
      className="w-full px-3 py-2.5 rounded-xl text-[13px] outline-none resize-none"
      style={{ background:T.bgSoft, border:`1px solid ${T.border}`, color:T.textPri }}
      onFocus={e=>e.target.style.borderColor=T.gold}
      onBlur={e=>e.target.style.borderColor=T.border}/>
  </div>
);

// ── Create Order Modal ────────────────────────────────────────────────────────
function CreateOrderModal({ onClose, onSave, saving }) {
  const [form, setForm] = useState(EMPTY_ORDER);
  const [step, setStep] = useState(1); // 1 = customer, 2 = product, 3 = review
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = () => {
    if (!form.customer.trim()) return alert("Customer name required!");
    if (!form.product) return alert("Product required!");
    if (!form.amount) return alert("Amount required!");
    onSave(form);
  };
  
  return (
   <div onClick={onClose} className="fixed inset-0 z-50 flex items-center justify-center px-0 sm:px-4" style={{ background:"rgba(0,0,0,0.5)" }}>
  <div onClick={(e) => e.stopPropagation()} className="w-full h-full sm:h-auto sm:max-w-2xl sm:max-h-[90vh] rounded-none sm:rounded-3xl overflow-hidden flex flex-col"
    style={{ background:T.card, boxShadow:"0 20px 60px rgba(0,0,0,0.2)" }}>

        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3.5 sm:py-4 flex-shrink-0"
          style={{ borderBottom:`1px solid ${T.border}` }}>
          <div className="min-w-0">
            <h2 className="text-[16px] sm:text-[18px] font-bold" style={{ color:T.textPri, fontFamily:T.serif }}>Create New Order</h2>
            <p className="text-[10.5px] sm:text-[11px] truncate" style={{ color:T.textSec }}>Step {step} of 3 — {["","Customer Details","Product & Payment","Review & Confirm"][step]}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none flex-shrink-0 ml-2">×</button>
        </div>

        {/* Step indicators */}
        <div className="flex items-center px-4 sm:px-6 py-3 gap-0 flex-shrink-0 overflow-x-auto" style={{ borderBottom:`1px solid ${T.border}` }}>
          {[1,2,3].map((s, i) => (
            <div key={s} className="flex items-center flex-1 last:flex-none min-w-fit">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center text-[11px] sm:text-[12px] font-bold transition-all flex-shrink-0"
                  style={{ background: step >= s ? T.gold : T.border, color: step >= s ? "white" : T.textSec }}>
                  {step > s ? "✓" : s}
                </div>
                <span className="text-[10px] sm:text-[11px] font-medium whitespace-nowrap" style={{ color: step >= s ? T.gold : T.textSec }}>
                  {["Customer","Product","Review"][i]}
                </span>
              </div>
              {i < 2 && <div className="flex-1 h-px mx-2 sm:mx-3 min-w-[16px]" style={{ background: step > s ? T.gold : T.border }}/>}
            </div>
          ))}
        </div>

        {/* Body */}
       <div data-lenis-prevent className="flex-1 overflow-y-auto p-4 sm:p-6">

          {/* Step 1 — Customer */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FInput label="Customer Name" value={form.customer} onChange={e=>set("customer",e.target.value)} placeholder="Khushi Verma" required />
                <FInput label="Email" value={form.email} onChange={e=>set("email",e.target.value)} placeholder="khushi@gmail.com" type="email" required />
                <FInput label="Phone" value={form.phone} onChange={e=>set("phone",e.target.value)} placeholder="+91 98200 11111" />
                <FInput label="City" value={form.city} onChange={e=>set("city",e.target.value)} placeholder="Mumbai" />
              </div>
              <FTextarea label="Delivery Address" value={form.address} onChange={e=>set("address",e.target.value)} placeholder="Full delivery address..." rows={2} />
            </div>
          )}

          {/* Step 2 — Product & Payment */}
          {step === 2 && (
            <div className="space-y-4">
              <FSelect label="Product" value={form.product} onChange={e=>set("product",e.target.value)} required>
                <option value="">— Select Product —</option>
                {PRODUCTS_LIST.map(p => <option key={p} value={p}>{p}</option>)}
              </FSelect>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FInput label="Amount (₹)" value={form.amount} onChange={e=>set("amount",e.target.value)} placeholder="1499" type="number" required />
                <FSelect label="Payment Status" value={form.pay} onChange={e=>set("pay",e.target.value)}>
                  {PAYMENT_LIST.map(p => <option key={p} value={p}>{p}</option>)}
                </FSelect>
                <FSelect label="Order Status" value={form.status} onChange={e=>set("status",e.target.value)}>
                  {STATUS_LIST.map(s => <option key={s} value={s}>{s}</option>)}
                </FSelect>
                <FInput label="Tracking Number" value={form.track} onChange={e=>set("track",e.target.value)} placeholder="TRK1234" />
              </div>
              <FTextarea label="Admin Notes (optional)" value={form.notes} onChange={e=>set("notes",e.target.value)} placeholder="Any special instructions..." rows={2} />
            </div>
          )}

          {/* Step 3 — Review */}
          {step === 3 && (
            <div className="space-y-4">
              <p className="text-[13px] font-semibold mb-3" style={{ color:T.textSec }}>Review order before saving:</p>
              <div className="grid grid-cols-1 xs:grid-cols-2 gap-3 rounded-2xl p-4 sm:p-5" style={{ background:T.bgSoft }}>
                {[
                  ["Customer", form.customer || "—"],
                  ["Email", form.email || "—"],
                  ["Phone", form.phone || "—"],
                  ["City", form.city || "—"],
                  ["Product", form.product || "—"],
                  ["Amount", form.amount ? `₹${form.amount}` : "—"],
                  ["Payment", form.pay],
                  ["Status", form.status],
                  ["Tracking", form.track || "—"],
                ].map(([l, v]) => (
                  <div key={l}>
                    <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color:T.textSec }}>{l}</p>
                    <p className="text-[13px] font-medium mt-0.5" style={{ color:T.textPri }}>{v}</p>
                  </div>
                ))}
                {form.address && (
                  <div className="col-span-1 xs:col-span-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color:T.textSec }}>Address</p>
                    <p className="text-[13px] font-medium mt-0.5" style={{ color:T.textPri }}>{form.address}</p>
                  </div>
                )}
                {form.notes && (
                  <div className="col-span-1 xs:col-span-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color:T.textSec }}>Notes</p>
                    <p className="text-[13px] font-medium mt-0.5" style={{ color:T.textPri }}>{form.notes}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3.5 sm:py-4 flex-shrink-0"
          style={{ borderTop:`1px solid ${T.border}` }}>
          <button onClick={onClose} disabled={saving}
            className="px-3.5 sm:px-4 py-2 rounded-xl text-[12.5px] sm:text-[13px] font-medium border disabled:opacity-50"
            style={{ borderColor:T.border, color:T.textSec }}>Cancel</button>
          <div className="flex gap-2">
            {step > 1 && (
              <button onClick={() => setStep(s => s - 1)} disabled={saving}
                className="px-3.5 sm:px-4 py-2 rounded-xl text-[12.5px] sm:text-[13px] font-medium border disabled:opacity-50"
                style={{ borderColor:T.border, color:T.textSec }}>← Back</button>
            )}
            {step < 3 ? (
              <button onClick={() => setStep(s => s + 1)}
                className="px-4 sm:px-5 py-2 rounded-xl text-[12.5px] sm:text-[13px] font-semibold text-white"
                style={{ background:T.gold }}>
                Next →
              </button>
            ) : (
              <button onClick={handleSave} disabled={saving}
                className="px-4 sm:px-5 py-2 rounded-xl text-[12.5px] sm:text-[13px] font-semibold text-white disabled:opacity-60"
                style={{ background:T.gold }}>
                {saving ? "Saving..." : "✓ Save Order"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Update Status Modal ────────────────────────────────────────────────────────
function UpdateStatusModal({ order, onClose, onUpdate, saving }) {
  const [status, setStatus] = useState(order.status);
  const [pay, setPay]       = useState(order.pay);
  const [track, setTrack]   = useState(order.track === "—" ? "" : order.track);
  return (
 <div onClick={onClose} className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background:"rgba(0,0,0,0.5)" }}>
  <div onClick={(e) => e.stopPropagation()} className="w-full max-w-xs sm:w-80 rounded-3xl overflow-hidden" style={{ background:T.card }}>  
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom:`1px solid ${T.border}` }}>
          <h3 className="text-[15px] font-bold" style={{ color:T.textPri, fontFamily:T.serif }}>Update Order</h3>
          <button onClick={onClose} className="text-gray-400 text-xl">×</button>
        </div>
        <div className="p-5 space-y-3">
          <FSelect label="Order Status" value={status} onChange={e=>setStatus(e.target.value)}>
            {STATUS_LIST.map(s=> <option key={s} value={s}>{s}</option>)}
          </FSelect>
          <FSelect label="Payment Status" value={pay} onChange={e=>setPay(e.target.value)}>
            {PAYMENT_LIST.map(p=> <option key={p} value={p}>{p}</option>)}
          </FSelect>
          <FInput label="Tracking Number" value={track} onChange={e=>setTrack(e.target.value)} placeholder="TRK1234" />
          <div className="flex gap-2 pt-1">
            <button onClick={() => onUpdate(order.dbId, { status, pay, track })} disabled={saving}
              className="flex-1 py-2.5 rounded-xl text-[13px] font-semibold text-white disabled:opacity-60"
              style={{ background:T.gold }}>{saving ? "Updating..." : "Update"}</button>
            <button onClick={onClose} disabled={saving}
              className="flex-1 py-2.5 rounded-xl text-[13px] border font-medium disabled:opacity-50"
              style={{ borderColor:T.border, color:T.textSec }}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Order Detail Modal (centered popup, scrollable body) ───────────────────────
function OrderDetailDrawer({ order, onClose, onUpdateClick }) {
  if (!order) return null;

  return (
   <div onClick={onClose} className="fixed inset-0 z-50 flex items-center justify-center px-0 sm:px-4" style={{ background: "rgba(0,0,0,0.5)" }}>
  <div
    onClick={(e) => e.stopPropagation()}
    className="w-full h-full sm:h-auto sm:max-w-lg sm:max-h-[90vh] rounded-none sm:rounded-3xl overflow-hidden flex flex-col"
    style={{ background: T.card, boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}
  >
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3.5 sm:py-4 flex-shrink-0" style={{ borderBottom:`1px solid ${T.border}` }}>
          <div className="min-w-0">
            <h2 className="text-[16px] sm:text-[17px] font-bold truncate" style={{ color:T.textPri, fontFamily:T.serif }}>Order {order.id}</h2>
            <p className="text-[11px] mt-0.5" style={{ color:T.textSec }}>
              {order.date}{order.time ? ` · ${order.time}` : ""}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none flex-shrink-0 ml-2">×</button>
        </div>

        {/* Scrollable body — sab info yahin, scroll ke saath */}
 
<div data-lenis-prevent className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5">

          {/* Status pills row */}
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill s={order.status} />
            <StatusPill s={paymentLabel(order)} />
            <span className="text-[13px] font-bold ml-auto" style={{ color:T.textPri }}>{order.amount}</span>
          </div>

          {/* Customer section */}
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider mb-2.5" style={{ color:T.gold }}>Customer</p>
            <div className="rounded-2xl p-4 space-y-3" style={{ background:T.bgSoft }}>
              <div className="flex items-center gap-3">
                <Avatar name={order.customer} />
                <div className="min-w-0">
                  <p className="text-[14px] font-semibold truncate" style={{ color:T.textPri }}>{order.customer || "—"}</p>
                  <p className="text-[11px] truncate" style={{ color:T.textSec }}>{order.email || "—"}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 pt-1">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color:T.textSec }}>Phone</p>
                  <p className="text-[13px] font-medium mt-0.5" style={{ color:T.textPri }}>{order.phone || "—"}</p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color:T.textSec }}>Email</p>
                  <p className="text-[13px] font-medium mt-0.5 break-words" style={{ color:T.textPri }}>{order.email || "—"}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Delivery address section */}
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider mb-2.5" style={{ color:T.gold }}>Delivery Address</p>
            <div className="rounded-2xl p-4 space-y-3" style={{ background:T.bgSoft }}>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color:T.textSec }}>Address</p>
                <p className="text-[13px] font-medium mt-0.5 break-words leading-relaxed" style={{ color:T.textPri }}>{order.address || "—"}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color:T.textSec }}>City</p>
                  <p className="text-[13px] font-medium mt-0.5" style={{ color:T.textPri }}>{order.city || "—"}</p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color:T.textSec }}>State</p>
                  <p className="text-[13px] font-medium mt-0.5" style={{ color:T.textPri }}>{order.state || "—"}</p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color:T.textSec }}>PIN Code</p>
                  <p className="text-[13px] font-medium mt-0.5" style={{ color:T.textPri }}>{order.pincode || "—"}</p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color:T.textSec }}>Country</p>
                  <p className="text-[13px] font-medium mt-0.5" style={{ color:T.textPri }}>India</p>
                </div>
              </div>
            </div>
          </div>

          {/* Order info section */}
         <div>
  <p className="text-[11px] font-bold uppercase tracking-wider mb-2.5" style={{ color: T.gold }}>Order Info</p>
  <div className="rounded-2xl p-4 grid grid-cols-2 gap-3" style={{ background: T.bgSoft }}>
    
    {/* Product */}
    <div className="col-span-2">
      <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: T.textSec }}>Product</p>
      <p className="text-[13px] font-medium mt-0.5" style={{ color: T.textPri }}>{order.product || "—"}</p>
    </div>

    {/* Personalised Name (Conditional) */}
    {order.personalised && (
      <div className="col-span-2">
        <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: T.gold }}>Personalised Name</p>
        <p className="text-[13px] font-semibold mt-0.5" style={{ color: T.textPri }}>✏️ {order.personalised}</p>
      </div>
    )}

    {/* Payment Method */}
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: T.textSec }}>Payment Method</p>
      <p className="text-[13px] font-medium mt-0.5" style={{ color: T.textPri }}>{order.payMethod || "—"}</p>
    </div>

    {/* Tracking */}
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: T.textSec }}>Tracking</p>
      <p className="text-[13px] font-medium mt-0.5" style={{ color: T.textPri }}>{order.track || "—"}</p>
    </div>

    {/* Date */}
   {/* Date */}
<div>
  <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: T.textSec }}>Date</p>
  <p className="text-[13px] font-medium mt-0.5" style={{ color: T.textPri }}>{order.date || "—"}</p>
</div>

{/* Time - Yahan ensure karein ki order.time properly pass ho raha hai */}
<div>
  <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: T.textSec }}>Time</p>
  <p className="text-[13px] font-medium mt-0.5" style={{ color: T.textPri }}>{order.time || "—"}</p>
</div>

  </div>
</div>
          

          {/* Notes */}
          {order.notes && (
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider mb-2.5" style={{ color:T.gold }}>Admin Notes</p>
              <div className="rounded-2xl p-4" style={{ background:T.bgSoft }}>
                <p className="text-[13px] font-medium break-words" style={{ color:T.textPri }}>{order.notes}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer — fixed, action buttons */}
        <div className="flex flex-wrap gap-2 px-4 sm:px-6 py-3.5 sm:py-4 flex-shrink-0" style={{ borderTop:`1px solid ${T.border}` }}>
          <GoldBtn small onClick={() => onUpdateClick(order)}>Update Status</GoldBtn>
          <GhostBtn>🖨 Print Invoice</GhostBtn>
          <GhostBtn
            onClick={() => {
              if (!order.phone) {
                alert("No phone number saved for this customer.");
                return;
              }
              window.open(buildWhatsAppLink(order), "_blank");
            }}
          >
            💬 WhatsApp Customer
          </GhostBtn>
          <GhostBtn>📦 Track Order</GhostBtn>
          <button onClick={onClose}
            className="ml-auto px-3 py-1.5 rounded-xl text-[12px] border"
            style={{ borderColor:T.border, color:T.textSec }}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN
// ══════════════════════════════════════════════════════════════════════════════
export default function OrdersAdmin() {
  const [orders, setOrders]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving]       = useState(false);

  const [tab, setTab]             = useState("all");
  const [search, setSearch]       = useState("");
  const [selected, setSel]        = useState([]);
  const [detail, setDetail]       = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [updateOrder, setUpdateOrder] = useState(null);
  const [delId, setDelId]         = useState(null);

  // ── Fetch orders from backend ───────────────────────────────────────────────
  const fetchOrders = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await apiFetch("/orders/list.php");
      setOrders(res.data || []);
    } catch (err) {
      setLoadError(err.message || "Failed to load orders.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, []);

  const STATS = [
    { icon:"📦", label:"Total Orders",    value: orders.length, sub:"All time", color:T.success },
    { icon:"⏳", label:"Pending",         value: orders.filter(o=>o.status==="Pending").length,    sub:"Needs action",  color:T.warning, bg:"#FFF8EC" },
    { icon:"🚚", label:"Shipped",         value: orders.filter(o=>o.status==="Shipped").length,    sub:"In transit",    color:"#5B7EE5", bg:"#EEF3FF" },
    { icon:"✅", label:"Delivered",       value: orders.filter(o=>o.status==="Delivered").length,  sub:"Fulfilled",     color:T.success },
    { icon:"❌", label:"Cancelled",       value: orders.filter(o=>o.status==="Cancelled").length,  sub:"Cancelled",     color:T.danger,  bg:"#FFF0F0" },
    { icon:"↩️", label:"Returned",        value: orders.filter(o=>o.status==="Returned").length,   sub:"Return requests",color:T.danger, bg:"#FFF0F0" },
  ];

  const TABS = [
    { id:"all",         label:"All",        count: orders.length },
    { id:"pending",     label:"Pending",    count: orders.filter(o=>o.status==="Pending").length },
    { id:"processing",  label:"Processing", count: orders.filter(o=>o.status==="Processing").length },
    { id:"shipped",     label:"Shipped",    count: orders.filter(o=>o.status==="Shipped").length },
    { id:"delivered",   label:"Delivered",  count: orders.filter(o=>o.status==="Delivered").length },
    { id:"cancelled",   label:"Cancelled",  count: orders.filter(o=>o.status==="Cancelled").length },
  ];

  const filtered = orders.filter(o => {
    const ms = !search || o.id.toLowerCase().includes(search.toLowerCase()) || o.customer.toLowerCase().includes(search.toLowerCase());
    const mt = tab === "all" || o.status.toLowerCase() === tab;
    return ms && mt;
  });

  // ── CRUD (backend connected) ────────────────────────────────────────────────
  const handleSaveOrder = async (formData) => {
    setSaving(true);
    try {
      await apiFetch("/orders/create.php", {
        method: "POST",
        body: {
          customer: formData.customer,
          email: formData.email,
          phone: formData.phone,
          city: formData.city,
          address: formData.address,
          items: [{ productName: formData.product, quantity: 1, price: Number(formData.amount) || 0 }],
          pay: formData.pay,
          status: formData.status,
          track: formData.track,
          notes: formData.notes,
        },
      });
      await fetchOrders();
      setShowCreate(false);
    } catch (err) {
      alert(err.message || "Failed to create order.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateStatus = async (dbId, updates) => {
    setSaving(true);
    try {
      await apiFetch("/orders/update.php", {
        method: "PUT",
        body: { id: dbId, ...updates },
      });
      await fetchOrders();
      setUpdateOrder(null);
    } catch (err) {
      alert(err.message || "Failed to update order.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (dbId) => {
    try {
      await apiFetch("/orders/delete.php", {
        method: "DELETE",
        body: { id: dbId },
      });
      await fetchOrders();
      setSel(s => s.filter(x => x !== dbId));
    } catch (err) {
      alert(err.message || "Failed to delete order.");
    } finally {
      setDelId(null);
    }
  };

  const handleBulkDelete = async () => {
    if (!selected.length) return;
    if (!confirm(`Delete ${selected.length} selected order(s)?`)) return;
    try {
      for (const dbId of selected) {
        await apiFetch("/orders/delete.php", { method: "DELETE", body: { id: dbId } });
      }
      await fetchOrders();
      setSel([]);
    } catch (err) {
      alert(err.message || "Failed to delete some orders.");
    }
  };

  return (
    <div>
      <PageHeader title="Orders" subtitle={`${orders.length} total orders`}>
        <GhostBtn>↓ Export CSV</GhostBtn>
        <GhostBtn className="hidden sm:inline-flex">🖨 Print Selected</GhostBtn>
        <GoldBtn onClick={() => setShowCreate(true)}>+ Create Order</GoldBtn>
      </PageHeader>

      {/* Loading / Error states */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 rounded-full animate-spin"
              style={{ border: `3px solid ${T.border}`, borderTopColor: T.gold }} />
            <p className="text-[12px]" style={{ color: T.textSec }}>Loading orders...</p>
          </div>
        </div>
      )}

      {!loading && loadError && (
        <Card className="p-6 text-center mb-5">
          <p className="text-[13px] font-semibold mb-2" style={{ color: T.danger }}>{loadError}</p>
          <button onClick={fetchOrders}
            className="px-4 py-2 rounded-xl text-[12px] font-semibold text-white"
            style={{ background: T.gold }}>
            Retry
          </button>
        </Card>
      )}

      {!loading && !loadError && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 sm:gap-2.5 md:gap-3 mb-4 sm:mb-6">
            {STATS.map(s => <StatCard key={s.label} {...s} />)}
          </div>

          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 mb-1">
            <Tabs tabs={TABS} active={tab} onChange={setTab} />
          </div>

          <Card>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 sm:gap-3 px-3.5 sm:px-5 py-3 sm:py-4" style={{ borderBottom:`1px solid ${T.border}` }}>
              <SearchBar value={search} onChange={setSearch} placeholder="Search by order ID or customer..." />
              <div className="flex gap-2">
                <select className="flex-1 sm:flex-none text-[11px] sm:text-[12px] px-2.5 sm:px-3 py-2 sm:py-2.5 rounded-xl outline-none"
                  style={{ border:`1px solid ${T.border}`, background:"#fff", color:T.textSec }}>
                  <option>All Payment</option><option>Paid</option><option>Pending</option><option>Failed</option>
                </select>
                <select className="flex-1 sm:flex-none text-[11px] sm:text-[12px] px-2.5 sm:px-3 py-2 sm:py-2.5 rounded-xl outline-none"
                  style={{ border:`1px solid ${T.border}`, background:"#fff", color:T.textSec }}>
                  <option>All Time</option><option>Today</option><option>This Week</option><option>This Month</option>
                </select>
                {selected.length > 0 && (
                  <button onClick={handleBulkDelete}
                    className="text-[11px] sm:text-[12px] px-3 py-2 rounded-xl font-medium whitespace-nowrap"
                    style={{ background:"#FFF0F0", color:T.danger }}>
                    🗑 Delete ({selected.length})
                  </button>
                )}
              </div>
            </div>

            {filtered.length === 0
              ? <EmptyState icon="📦" title="No orders found" sub="Try changing filters or create a new order." action="Create Order" onAction={() => setShowCreate(true)} />
              : (
                <>
                  {/* Desktop / tablet table */}
                  <div className="overflow-x-auto hidden sm:block">
                    <table className="w-full">
                      <thead>
                        <tr style={{ borderBottom:`1px solid ${T.border}` }}>
                          <Th>
                            <input type="checkbox" style={{ accentColor:T.gold }}
                              checked={selected.length === filtered.length && filtered.length > 0}
                              onChange={e => setSel(e.target.checked ? filtered.map(o=>o.dbId) : [])} />
                          </Th>
                         <Th>Order ID</Th><Th>Customer</Th><Th>Product</Th>
<Th>Date</Th><Th>Payment</Th><Th>Status</Th>
<Th>Amount</Th><Th>Personalised</Th><Th>Actions</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((o, i) => (
                          <Tr key={o.dbId} last={i === filtered.length - 1}>
                            <Td>
                              <input type="checkbox" checked={selected.includes(o.dbId)} style={{ accentColor:T.gold }}
                                onChange={e => setSel(s => e.target.checked ? [...s,o.dbId] : s.filter(x=>x!==o.dbId))} />
                            </Td>
                            <Td><span className="text-[12px] font-semibold font-mono" style={{ color:T.textPri }}>{o.id}</span></Td>
                            <Td>
                              <div className="flex items-center gap-2.5">
                                <Avatar name={o.customer} />
                                <div>
                                  <p className="text-[12px] font-medium" style={{ color:T.textPri }}>{o.customer}</p>
                                  <p className="text-[10px]" style={{ color:T.textSec }}>{o.city}</p>
                                </div>
                              </div>
                            </Td>
                            <Td><span className="text-[11px] max-w-[140px] truncate inline-block" style={{ color:T.textSec }}>{o.product}</span></Td>
                            <Td><span className="text-[11px]" style={{ color:T.textSec }}>{o.date}</span></Td>
                            <Td><StatusPill s={paymentLabel(o)} /></Td>
                            <Td><StatusPill s={o.status} /></Td>
                           <Td><span className="text-[13px] font-bold" style={{ color:T.textPri }}>{o.amount}</span></Td>
<Td>
  {o.personalised
    ? <span className="text-[11px] font-medium px-2 py-1 rounded-full" style={{ background: "#FFF8EC", color: T.warning }}>✏️ {o.personalised}</span>
    : <span className="text-[11px]" style={{ color: T.textSec }}>—</span>}
</Td>
                            <Td>
                              <div className="flex gap-1">
                                <button onClick={() => setDetail(o)}
                                  className="px-2.5 py-1.5 rounded-lg text-[11px] font-medium border hover:bg-amber-50"
                                  style={{ borderColor:T.border, color:T.textSec }}>View</button>
                                <button onClick={() => setUpdateOrder(o)}
                                  className="px-2.5 py-1.5 rounded-lg text-[11px] font-medium border hover:bg-amber-50"
                                  style={{ borderColor:T.border, color:T.textSec }}>Edit</button>
                                <button onClick={() => setDelId(o.dbId)}
                                  className="px-2.5 py-1.5 rounded-lg text-[11px] font-medium border hover:bg-red-50"
                                  style={{ borderColor:T.border, color:T.danger }}>🗑</button>
                              </div>
                            </Td>
                          </Tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile card list */}
                  <div className="sm:hidden divide-y" style={{ borderColor: T.border }}>
                    {filtered.map(o => (
                      <div key={o.dbId} className="p-3.5" onClick={() => setDetail(o)}>
                        <div className="flex items-start gap-3">
                          <Avatar name={o.customer} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-[12px] font-semibold font-mono" style={{ color:T.textPri }}>{o.id}</span>
                              <StatusPill s={o.status} />
                            </div>
                            <p className="text-[12.5px] font-medium mt-0.5 truncate" style={{ color:T.textPri }}>{o.customer}</p>
                            <p className="text-[10.5px]" style={{ color:T.textSec }}>{o.city} · {o.date}</p>
                            <p className="text-[10.5px] mt-1 truncate" style={{ color:T.textSec }}>{o.product}</p>
                            <div className="flex items-center justify-between mt-2">
                              <div className="flex items-center gap-2">
                                <span className="text-[13px] font-bold" style={{ color:T.textPri }}>{o.amount}</span>
                                <StatusPill s={paymentLabel(o)} />
                              </div>
                              <div className="flex gap-1.5" onClick={e => e.stopPropagation()}>
                                <button onClick={() => setUpdateOrder(o)}
                                  className="px-2.5 py-1 rounded-lg text-[10.5px] border"
                                  style={{ borderColor:T.border, color:T.textSec }}>Edit</button>
                                <button onClick={() => setDelId(o.dbId)}
                                  className="px-2.5 py-1 rounded-lg text-[10.5px] border"
                                  style={{ borderColor:T.border, color:T.danger }}>🗑</button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

            <div className="px-3.5 sm:px-5 py-3 flex flex-col sm:flex-row gap-1 sm:gap-0 items-start sm:items-center justify-between" style={{ borderTop:`1px solid ${T.border}` }}>
              <p className="text-[10.5px] sm:text-[11px]" style={{ color:T.textSec }}>Showing {filtered.length} of {orders.length} orders</p>
            </div>
          </Card>
        </>
      )}

      {/* ── Create Order Modal ── */}
      {showCreate && (
        <CreateOrderModal
          saving={saving}
          onClose={() => { if (!saving) setShowCreate(false); }}
          onSave={handleSaveOrder}
        />
      )}

      {/* ── Order Detail Modal (popup, not side drawer) ── */}
      {detail && (
        <OrderDetailDrawer
          order={detail}
          onClose={() => setDetail(null)}
          onUpdateClick={(o) => { setDetail(null); setUpdateOrder(o); }}
        />
      )}

      {/* ── Update Status Modal ── */}
      {updateOrder && (
        <UpdateStatusModal
          order={updateOrder}
          saving={saving}
          onClose={() => { if (!saving) setUpdateOrder(null); }}
          onUpdate={handleUpdateStatus}
        />
      )}

      {/* ── Delete Confirm ── */}
      {delId && (
        <ConfirmModal
          title="Delete Order?"
          message={`Order ${orders.find(o=>o.dbId===delId)?.id || ""} will be permanently deleted.`}
          onConfirm={() => handleDelete(delId)}
          onCancel={() => setDelId(null)}
        />
      )}
    </div>
  );
}